import React from "react";
import "./PaymentBreakup.css";
import { currencyFormatter, formatDateTime, textCapitalize } from "../../../Helpers/Utils";

/*
 * Exact payment breakup for one booking, with the working shown beside every
 * amount. Uses the pricing snapshot stored at booking time
 * (preBookDetails.pricing) when it exists, and falls back to working
 * backwards from the stored fees for bookings created before it did.
 */

const money = (value) => currencyFormatter(value ?? 0);
const num = (value) => Number(value) || 0;
const round2 = (value) => Math.round(num(value) * 100) / 100;
const credit = (value) => `-${money(Math.abs(num(value)))}`;
const PAID_STATES = ["captured", "partially_refunded", "refunded"];

const Section = ({ title, note, rows }) => (
    <div className="bv-bk-section">
        <h3 className="bv-bk-title">{title}</h3>
        {note && <p className="bv-muted">{note}</p>}
        <div className="bv-table-wrap">
            <table className="bv-table bv-bk-table">
                <thead><tr><th scope="col">Item</th><th scope="col">Calculation</th><th scope="col" className="is-r">Amount</th></tr></thead>
                <tbody>
                    {rows.filter(Boolean).map((row) => (
                        <tr key={row.key} className={row.kind ? `bv-bk-${row.kind}` : ""}>
                            <th scope="row">{row.label}</th>
                            <td className="bv-bk-calc">{row.calc || ""}</td>
                            <td className={`bv-num is-r${row.credit ? " bv-bk-credit" : ""}`}>{row.credit ? credit(row.amount) : (typeof row.amount === "string" ? row.amount : money(row.amount))}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const PaymentBreakup = ({ booking, payments = [], refunds = [] }) => {
    const pre = booking.preBookDetails || {};
    const snap = pre.pricing || null;
    const pack = pre.package || {};
    const perHour = num(pack.perHourPrice);
    const perHourKm = num(pack.perHourKm);
    const hours = snap?.hours ?? (((new Date(pre.endDate) - new Date(pre.startDate)) / 3600000) || 0);
    const coupon = pre.coupon || null;
    const couponOn = (key) => num((coupon?.lines || []).find((line) => line.appliesTo === key)?.discountAmount);
    const coinsSpent = num(pre.coins?.spent);
    const paidRows = payments.filter((p) => PAID_STATES.includes(p.status));

    /* ---------- rental ---------- */
    const rentalNet = num(pre.rentalFee);
    const rentalGross = snap?.rental?.gross ?? round2(rentalNet + couponOn("Rent") + coinsSpent);
    const rate = snap?.rental?.rate ?? (hours > 0 ? round2(rentalGross / hours) : perHour);
    const peak = snap?.peakPercentage ?? (perHour > 0 && rate > perHour ? Math.round((rate / perHour - 1) * 100) : 0);
    const couponLabel = coupon ? `Coupon ${coupon.couponCode} (${coupon.discountType === "percentage" ? `${coupon.discountValue}%` : `flat ${money(coupon.discountValue)}`})` : "";

    /* ---------- protection ---------- */
    const tp = pre.tripProtection || {};
    const tpFee = num(tp.feeCharged);
    const tpPct = num(tp.feePercentage);
    const insurance = snap?.protection?.insuranceForDuration;
    const tpCalc = insurance != null
        ? `Insurance for ${hours} hr ${money(insurance)} + ${tpPct}% fee ${money(Math.ceil((insurance * tpPct) / 100))}`
        : `${tp.name || "Protection"} at ${tpPct}% (stored as charged)`;

    /* ---------- delivery and pickup ---------- */
    const leg = (details, legSnap, name) => {
        if (!details) return [];
        const net = num(details.fee ?? legSnap?.net);
        const gross = legSnap?.gross ?? net;
        const discount = legSnap?.discount ?? 0;
        const bands = num(details.bands ?? legSnap?.bands);
        const distance = details.distanceKm ?? details.distance ?? legSnap?.distance;
        const perBand = bands > 0 ? round2(gross / bands) : 0;
        const rows = [{ key: `${name}-g`, label: `${name} fee`, calc: bands > 0 ? `${bands} band${bands === 1 ? "" : "s"} x ${money(perBand)} (${distance} km)` : `${distance ?? 0} km`, amount: gross }];
        if (discount > 0) rows.push({ key: `${name}-d`, label: `${name} coupon discount`, calc: couponLabel, amount: discount, credit: true });
        return rows;
    };
    const deliveryNet = num(pre.deliveryDetails?.fee ?? snap?.delivery?.net);
    const pickupNet = num(pre.pickupDetails?.fee ?? snap?.pickup?.net);

    const convenience = num(pre.convenienceFee);
    const platform = num(pre.platformFee);
    const payableNow = round2(rentalNet + tpFee + deliveryNet + pickupNet + convenience + platform);

    const deposit = num(pre.securityDeposit);
    const depositDiscount = snap?.securityDeposit?.discount ?? couponOn("SD");
    const depositGross = snap?.securityDeposit?.gross ?? round2(deposit + depositDiscount);
    const bookingTotal = round2(payableNow + deposit);
    const bookingPaid = round2(paidRows.filter((p) => p.paymentFor === "Booking").reduce((t, p) => t + num(p.totalAmount), 0));
    const storedTotal = booking.totalAmount != null ? num(booking.totalAmount) : null;

    const bookingRows = [
        { key: "rent", label: "Rental", calc: `${hours} hr x ${money(rate)}/hr${peak > 0 ? ` (base ${money(perHour)} + ${peak}% peak)` : ""}`, amount: rentalGross },
        couponOn("Rent") > 0 && { key: "rent-c", label: "Coupon on rent", calc: couponLabel, amount: couponOn("Rent"), credit: true },
        coinsSpent > 0 && { key: "coins", label: "Coins applied", calc: `${coinsSpent} coins at 1 coin = ${money(1)}`, amount: coinsSpent, credit: true },
        { key: "rent-n", label: "Rental payable", calc: `${money(rentalGross)} - ${money(couponOn("Rent") + coinsSpent)}`, amount: rentalNet, kind: "sub" },
        { key: "tp", label: `Trip protection${tp.name ? `, ${tp.name}` : ""}`, calc: tpCalc, amount: tpFee },
        ...leg(pre.deliveryDetails, snap?.delivery, "Delivery"),
        ...leg(pre.pickupDetails, snap?.pickup, "Pickup"),
        { key: "conv", label: "Convenience fee", calc: "Fixed fee from site settings", amount: convenience },
        { key: "plat", label: "Platform fee", calc: "Fixed fee from site settings", amount: platform },
        { key: "now", label: "Payable now", calc: "Rental + protection + delivery + pickup + fees", amount: payableNow, kind: "sub" },
        { key: "sd-g", label: "Security deposit", calc: `Per-day deposit banded over ${hours} hr`, amount: depositGross },
        depositDiscount > 0 && { key: "sd-c", label: "Coupon on deposit", calc: couponLabel, amount: depositDiscount, credit: true },
        { key: "sd", label: "Security deposit payable", calc: "Refundable after the trip", amount: deposit, kind: "sub" },
        { key: "total", label: "Booking total", calc: `${money(payableNow)} + ${money(deposit)}`, amount: bookingTotal, kind: "total" },
    ];
    const totalMismatch = storedTotal != null && Math.abs(storedTotal - bookingTotal) > 0.5;
    const bookingNote = totalMismatch
        ? `The working above adds up to ${money(bookingTotal)} but the booking records ${money(storedTotal)}. The difference is ${money(Math.abs(storedTotal - bookingTotal))}.`
        : bookingPaid > 0 ? `Captured Booking payment: ${money(bookingPaid)}${Math.abs(bookingPaid - bookingTotal) > 0.5 ? `, which differs from the total by ${money(Math.abs(bookingPaid - bookingTotal))}.` : ", matching the total."}` : "No Booking payment captured yet.";

    /* ---------- extensions ---------- */
    const extensions = payments.filter((p) => p.paymentFor === "Extension");
    const extensionRows = extensions.flatMap((p) => {
        const b = p.breakup;
        const status = PAID_STATES.includes(p.status) ? "Paid" : textCapitalize(String(p.status || "").replace(/_/g, " "));
        const head = `Extension ${p.orderId}`;
        const rows = [];
        if (b) {
            rows.push({ key: `${p.orderId}-r`, label: head, calc: `${p.extraHours} hr x ${money(b.rate)}/hr`, amount: b.rental });
            if (num(b.protectionTopUp) > 0) rows.push({ key: `${p.orderId}-p`, label: "Protection top-up", calc: "Protection fee for the longer trip minus the fee already paid", amount: b.protectionTopUp });
            rows.push({ key: `${p.orderId}-t`, label: "Extension total", calc: `${status}${p.createdAt ? `, ${formatDateTime(p.createdAt)}` : ""}`, amount: p.totalAmount, kind: "sub" });
        } else {
            rows.push({ key: `${p.orderId}-t`, label: head, calc: `${p.extraHours ? `+${p.extraHours} hr, ` : ""}${status}. Working not stored for this order`, amount: p.totalAmount, kind: "sub" });
        }
        return rows;
    });

    /* ---------- fines and settlement ---------- */
    const settlement = booking.settlement || null;
    const bd = settlement?.breakdown || null;
    const fd = booking.fineDetails || {};
    let fineRows = [];
    if (settlement) {
        if (bd) {
            // Booking Flow v2: late hours, km and FASTag are always priced by the server (never waived or
            // typed in); a goodwill discount line is the only way to charge less.
            const blocks = bd.excessKm?.hourBlocks ?? (perHourKm > 0 ? Math.ceil(num(bd.excessKm?.quantity) / perHourKm) : 0);
            const rate = bd.excessKm?.rate ?? bd.excessHour?.rate ?? perHour;
            fineRows = [
                (bd.excessKm?.quantity > 0) && { key: "km", label: "Excess km", calc: `${bd.excessKm.quantity} km / ${perHourKm} km per hr = ${blocks} hr x ${money(rate)}`, amount: bd.excessKm.amount },
                (bd.excessHour?.quantity > 0) && { key: "hr", label: "Late hours", calc: `${bd.excessHour.quantity} hr x ${money(bd.excessHour.rate ?? perHour)}${bd.excessHour.lateMinutes ? ` (returned ${bd.excessHour.lateMinutes} min late; free within the grace, then every started hour)` : ""}`, amount: bd.excessHour.amount },
                (bd.excessFastag?.quantity > 0) && { key: "tag", label: "FASTag shortfall", calc: `${money(bd.excessFastag.quantity)} lower than at the start`, amount: bd.excessFastag.amount },
                ...(bd.excessFuel?.waived ? [{ key: "fuel", label: "Fuel shortfall", calc: "Covered by protection", amount: 0 }] : (fd.excessFuel || []).filter((f) => num(f.amount) > 0).map((f, i) => ({ key: `fuel${i}`, label: `Fuel, ${f.fuelType}`, calc: `${f.percentage}% short, amount entered by staff`, amount: f.amount }))),
                num(bd.damage?.amount) > 0 && { key: "dmg", label: "Damage", calc: num(bd.damage.repairCost) > 0 ? `${money(bd.damage.repairCost)} repair - ${money(bd.damage.coverAmount)} trip protection cover${booking.damage?.damageStatus ? ` (${textCapitalize(String(booking.damage.damageStatus))})` : ""}` : "Damage above the trip protection cover", amount: bd.damage.amount },
                ...(fd.fines || []).map((f, i) => ({ key: `misc${i}`, label: f.name || "Other fine", calc: "Entered by staff", amount: f.amount })),
                ...(fd.discounts || []).map((d, i) => ({ key: `disc${i}`, label: "Goodwill discount", calc: d.reason || "", amount: d.amount, credit: true })),
            ];
        } else {
            fineRows = [
                ...(fd.excessFuel || []).filter((f) => num(f.amount) > 0).map((f, i) => ({ key: `fuel${i}`, label: `Fuel, ${f.fuelType}`, calc: `${f.percentage}% short`, amount: f.amount })),
                ...(fd.fines || []).map((f, i) => ({ key: `misc${i}`, label: f.name || "Other fine", calc: "Entered by staff", amount: f.amount })),
                num(settlement.deductibleCharged) > 0 && { key: "dmg", label: "Damage deductible", calc: "", amount: settlement.deductibleCharged },
            ];
        }
        const fineTotal = num(settlement.fineTotal);
        const held = num(bd?.securityDeposit ?? deposit);
        fineRows.push({ key: "fine", label: "Total fine", calc: num(settlement.discountTotal) > 0 ? `${money(settlement.subtotal)} charges - ${money(settlement.discountTotal)} discount` : "Sum of the lines above", amount: fineTotal, kind: "sub" });
        fineRows.push({ key: "held", label: "Security deposit held", calc: "Paid with the booking", amount: held, credit: true });
        fineRows.push(fineTotal > held
            ? { key: "res", label: "Customer pays", calc: `${money(fineTotal)} - ${money(held)}, settlement order`, amount: fineTotal - held, kind: "total" }
            : { key: "res", label: "Deposit refunded", calc: `${money(held)} - ${money(fineTotal)}${settlement.depositRefundId ? `, refund ${settlement.depositRefundId}` : ""}`, amount: settlement.depositRefundAmount ?? held - fineTotal, kind: "total" });
        fineRows = fineRows.filter(Boolean);
    }

    /* ---------- cancellation ---------- */
    const refundCalc = booking.cancelDetails?.refund || null;
    const cancelLabel = { rental: "Rental", securityDeposit: "Security deposit", deliveryFee: "Delivery fee", pickupFee: "Pickup fee", protectionFee: "Trip protection", platformFee: "Platform fee", convenienceFee: "Convenience fee" };
    const cancelRows = refundCalc ? [
        ...(refundCalc.lines || []).map((line, i) => ({ key: `c${i}`, label: cancelLabel[line.component] || textCapitalize(String(line.component)), calc: `${money(line.paid)} x ${line.percentage ?? 0}% refundable`, amount: line.refund })),
        { key: "ct", label: "Total refund", calc: `${money(refundCalc.totalPaid)} paid, ${money(refundCalc.forfeited)} forfeited${refundCalc.hoursBeforeStart != null ? `, cancelled ${refundCalc.hoursBeforeStart} hr before pickup` : ""}`, amount: refundCalc.totalRefund, kind: "total" },
    ] : [];

    /* ---------- reconciliation ---------- */
    const sumFor = (kind) => round2(paidRows.filter((p) => p.paymentFor === kind).reduce((t, p) => t + num(p.totalAmount), 0));
    const paidTotal = round2(paidRows.reduce((t, p) => t + num(p.totalAmount), 0));
    const refundedTotal = round2(payments.reduce((t, p) => t + num(p.refundedAmount), 0));
    const open = round2(payments.filter((p) => p.status === "created" && !(p.paymentFor === "Booking" && booking.bookingStage !== "Initiated")).reduce((t, p) => t + num(p.totalAmount), 0));
    const reconRows = [
        { key: "pb", label: "Booking payments received", calc: `${paidRows.filter((p) => p.paymentFor === "Booking").length} payment(s)`, amount: sumFor("Booking") },
        sumFor("Extension") > 0 && { key: "pe", label: "Extension payments received", calc: `${paidRows.filter((p) => p.paymentFor === "Extension").length} payment(s)`, amount: sumFor("Extension") },
        sumFor("Settlement") > 0 && { key: "ps", label: "Settlement payment received", calc: "Fine above the deposit", amount: sumFor("Settlement") },
        { key: "pt", label: "Total received", calc: "", amount: paidTotal, kind: "sub" },
        { key: "rf", label: "Refunded to customer", calc: `${refunds.length} refund(s)`, amount: refundedTotal, credit: true },
        { key: "net", label: "Net kept", calc: `${money(paidTotal)} - ${money(refundedTotal)}`, amount: round2(paidTotal - refundedTotal), kind: "total" },
        open > 0 && { key: "open", label: "Still to collect", calc: "Open orders", amount: open },
    ];

    return (
        <div className="bv-bk">
            <Section title="Booking charges" note={bookingNote} rows={bookingRows} />
            {extensionRows.length > 0 && <Section title="Extensions" rows={extensionRows} />}
            {fineRows.length > 0 && <Section title="Trip-end fines and settlement" rows={fineRows} />}
            {cancelRows.length > 0 && <Section title="Cancellation refund" rows={cancelRows} />}
            <Section title="Money in and out" rows={reconRows} />
        </div>
    );
};

export default PaymentBreakup;
